import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  rosterRoles,
  type AthleteView,
  type MatchRosterView,
  type MatchView,
  type PageResponse,
  type RosterExportView,
  type SupportedLocale,
} from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { apiRequest } from './api';
import { athleteDisplayName, formatCalendarDate } from './athletes';
import { errorKeyOf, type ErrorKey } from './BillingForms';
import {
  emptyRosterEntryForm,
  finalizeRosterRequest,
  reopenRosterRequest,
  rosterActionPath,
  rosterEntryChanged,
  rosterEntryFormOf,
  rosterEntryRequest,
  rosterPath,
  rosterRoleKeys,
  rosterRoleOf,
  rosterStatusKeys,
  type RosterEntryForm,
} from './matchRoster';
import { RemoteSelect, type RemoteOption } from './RemoteSelect';
import { RosterEligibilitySection } from './RosterEligibilitySection';
import { formatCheckedAt } from './rosterEligibility';
import {
  rosterExportBlockerText,
  rosterExportCsv,
  rosterExportFileName,
  rosterExportPath,
} from './rosterExport';

export interface MatchRosterSectionProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}`. */
  base: string;
  csrfToken: string;
  match: MatchView;
  onClose: () => void;
}

interface EditingEntry {
  athleteId: string;
  form: RosterEntryForm;
}

/**
 * The draft roster of one match. A registrar searches an athlete, chooses the role in this match and
 * an optional shirt number, and changes or removes entries; every change is one request that is
 * saved at once and checked against the roster version last shown. After the match is completed or
 * cancelled the roster is shown but cannot change. The eligibility check of the roster is shown below
 * the entries. A registrar then finalizes the roster (after it was checked again on the server, with a
 * statement when something still needs review) and reopens a finalized one with a reason.
 */
export function MatchRosterSection({
  locale,
  base,
  csrfToken,
  match,
  onClose,
}: MatchRosterSectionProps): React.JSX.Element {
  const [roster, setRoster] = useState<MatchRosterView | null>(null);
  const [form, setForm] = useState<RosterEntryForm>(emptyRosterEntryForm);
  const [athleteId, setAthleteId] = useState('');
  const [formKey, setFormKey] = useState(0);
  const [editing, setEditing] = useState<EditingEntry | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [message, setMessage] = useState<TranslationKey | null>(null);
  const [action, setAction] = useState<'finalize' | 'reopen' | null>(null);
  const [statement, setStatement] = useState('');
  const [reason, setReason] = useState('');
  const [finalizeKey, setFinalizeKey] = useState(() => crypto.randomUUID());
  const [exportResult, setExportResult] = useState<RosterExportView | null>(null);

  const load = useCallback(async () => {
    try {
      setRoster(await apiRequest<MatchRosterView>(rosterPath(base, match.id)));
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base, match.id]);

  useEffect(() => {
    setRoster(null);
    setEditing(null);
    setErrorKey(null);
    setMessage(null);
    setAction(null);
    setStatement('');
    setReason('');
    setExportResult(null);
    void load();
  }, [load]);

  const findAthletes = useCallback(
    async (text: string): Promise<RemoteOption[]> => {
      const page = await apiRequest<PageResponse<AthleteView>>(
        `${base}/athletes?limit=20&q=${encodeURIComponent(text)}`,
      );
      return page.items.map((athlete) => ({
        id: athlete.id,
        label: `${athleteDisplayName(athlete, locale)} (${formatCalendarDate(athlete.dateOfBirth, locale)})`,
      }));
    },
    [base, locale],
  );

  async function change(
    action: (version: number) => Promise<MatchRosterView>,
    successKey: TranslationKey,
  ): Promise<boolean> {
    if (roster === null) return false;
    setErrorKey(null);
    setMessage(null);
    try {
      setRoster(await action(roster.version));
      setMessage(successKey);
      return true;
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      // A version conflict or a match closed meanwhile: show what the server holds now.
      await load();
      return false;
    }
  }

  const addRequest = athleteId === '' ? null : rosterEntryRequest(form);

  async function add(): Promise<void> {
    if (addRequest === null) return;
    const added = await change(
      (version) =>
        apiRequest<MatchRosterView>(rosterPath(base, match.id, athleteId), {
          method: 'PUT',
          body: addRequest,
          csrfToken,
          ifMatchVersion: version,
        }),
      'roster.add.done',
    );
    if (added) {
      setForm(emptyRosterEntryForm);
      setAthleteId('');
      setFormKey((current) => current + 1);
    }
  }

  async function save(entry: EditingEntry): Promise<void> {
    const request = rosterEntryRequest(entry.form);
    if (request === null) return;
    const saved = await change(
      (version) =>
        apiRequest<MatchRosterView>(rosterPath(base, match.id, entry.athleteId), {
          method: 'PUT',
          body: request,
          csrfToken,
          ifMatchVersion: version,
        }),
      'roster.edit.done',
    );
    if (saved) setEditing(null);
  }

  async function remove(removedAthleteId: string): Promise<void> {
    await change(
      (version) =>
        apiRequest<MatchRosterView>(rosterPath(base, match.id, removedAthleteId), {
          method: 'DELETE',
          csrfToken,
          ifMatchVersion: version,
        }),
      'roster.remove.done',
    );
    setEditing(null);
  }

  async function finalizeRoster(): Promise<void> {
    const finalized = await change(
      (version) =>
        apiRequest<MatchRosterView>(rosterActionPath(base, match.id, 'finalize'), {
          method: 'POST',
          body: finalizeRosterRequest(statement),
          csrfToken,
          ifMatchVersion: version,
          idempotencyKey: finalizeKey,
        }),
      'roster.finalize.done',
    );
    if (finalized) {
      setAction(null);
      setStatement('');
      setFinalizeKey(crypto.randomUUID());
    }
  }

  async function exportRoster(): Promise<void> {
    setErrorKey(null);
    setMessage(null);
    try {
      const exported = await apiRequest<RosterExportView>(rosterExportPath(base, match.id));
      const url = URL.createObjectURL(
        new Blob([rosterExportCsv(exported, locale)], { type: 'text/csv;charset=utf-8' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = rosterExportFileName(exported);
      link.click();
      URL.revokeObjectURL(url);
      setExportResult(exported);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  const reopenRequest = reopenRosterRequest(reason);

  async function reopenRoster(): Promise<void> {
    if (reopenRequest === null) return;
    const reopened = await change(
      (version) =>
        apiRequest<MatchRosterView>(rosterActionPath(base, match.id, 'reopen'), {
          method: 'POST',
          body: reopenRequest,
          csrfToken,
          ifMatchVersion: version,
        }),
      'roster.reopen.done',
    );
    if (reopened) {
      setAction(null);
      setReason('');
    }
  }

  const roleOptions = rosterRoles.map((role) => (
    <MenuItem key={role} value={role}>
      {translate(locale, rosterRoleKeys[role])}
    </MenuItem>
  ));

  return (
    <Box
      component="section"
      aria-label={translate(locale, 'roster.title')}
      sx={{ mt: 1.5, pl: { xs: 1, sm: 2 }, borderLeft: 2, borderColor: 'divider' }}
    >
      <Typography variant="subtitle2" component="h4">
        {translate(locale, 'roster.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {translate(locale, 'roster.hint')}
      </Typography>
      {roster !== null && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <Chip
            size="small"
            variant="outlined"
            color={roster.status === 'finalized' ? 'success' : 'default'}
            label={translate(locale, rosterStatusKeys[roster.status])}
          />
          {roster.finalizedAt !== null && (
            <Typography variant="body2" color="text.secondary">
              {translate(locale, 'roster.finalizedAt').replace(
                '{finalizedAt}',
                formatCheckedAt(roster.finalizedAt, locale),
              )}
              {roster.reviewAcknowledged && ` ${translate(locale, 'roster.acknowledged')}`}
            </Typography>
          )}
        </Box>
      )}
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {message !== null && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {translate(locale, message)}
        </Alert>
      )}
      {roster === null && errorKey === null && (
        <Typography>{translate(locale, 'roster.loading')}</Typography>
      )}
      {roster?.entries.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          {translate(locale, 'roster.empty')}
        </Typography>
      )}
      {roster !== null && !roster.canChange && match.status !== 'scheduled' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {translate(locale, 'roster.locked')}
        </Typography>
      )}
      {roster?.status === 'finalized' && match.status === 'scheduled' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {translate(locale, 'roster.frozen')}
        </Typography>
      )}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0, mb: 2 }}>
        {(roster?.entries ?? []).map((entry) => {
          const open = editing?.athleteId === entry.athlete.id ? editing : null;
          return (
            <Box component="li" key={entry.athlete.id}>
              <Typography variant="body2">
                {athleteDisplayName(entry.athlete, locale)} ·{' '}
                {translate(locale, rosterRoleKeys[entry.role])} ·{' '}
                {entry.shirtNumber === null
                  ? translate(locale, 'roster.noShirt')
                  : `${translate(locale, 'roster.shirt')} ${String(entry.shirtNumber)}`}
              </Typography>
              {roster?.canChange === true && open === null && (
                <Box sx={{ mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setEditing({ athleteId: entry.athlete.id, form: rosterEntryFormOf(entry) });
                    }}
                  >
                    {translate(locale, 'roster.edit')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      void remove(entry.athlete.id);
                    }}
                  >
                    {translate(locale, 'roster.remove')}
                  </Button>
                </Box>
              )}
              {open !== null && (
                <Stack
                  component="form"
                  spacing={1.5}
                  sx={{ mt: 1, maxWidth: 420 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    void save(open);
                  }}
                >
                  <TextField
                    required
                    select
                    size="small"
                    label={translate(locale, 'roster.field.role')}
                    value={open.form.role}
                    onChange={(event) => {
                      setEditing({
                        ...open,
                        form: { ...open.form, role: rosterRoleOf(event.target.value) },
                      });
                    }}
                  >
                    {roleOptions}
                  </TextField>
                  <TextField
                    size="small"
                    label={translate(locale, 'roster.field.shirt')}
                    value={open.form.shirtNumber}
                    onChange={(event) => {
                      setEditing({
                        ...open,
                        form: { ...open.form, shirtNumber: event.target.value },
                      });
                    }}
                    slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 2 } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="small"
                      disabled={!rosterEntryChanged(open.form, entry)}
                    >
                      {translate(locale, 'roster.edit.submit')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setEditing(null);
                      }}
                    >
                      {translate(locale, 'roster.close')}
                    </Button>
                  </Box>
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
      {roster !== null && (
        <RosterEligibilitySection
          locale={locale}
          base={base}
          csrfToken={csrfToken}
          matchId={match.id}
          rosterVersion={roster.version}
          rosterStatus={roster.status}
        />
      )}
      {roster !== null && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {translate(locale, 'roster.export.hint')}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              void exportRoster();
            }}
          >
            {translate(locale, 'roster.export')}
          </Button>
          {exportResult !== null && (
            <Alert severity={exportResult.officialReady ? 'success' : 'warning'} sx={{ mt: 1 }}>
              {exportResult.officialReady ? (
                translate(locale, 'roster.export.ready')
              ) : (
                <>
                  {translate(locale, 'roster.export.blocked')}
                  <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
                    {exportResult.blockers.map((blocker) => (
                      <li key={`${blocker.code}-${blocker.key ?? ''}`}>
                        {rosterExportBlockerText(locale, blocker)}
                      </li>
                    ))}
                  </Box>
                </>
              )}
              {exportResult.hiddenEntryCount > 0 &&
                ` ${translate(locale, 'roster.export.hidden').replace('{count}', String(exportResult.hiddenEntryCount))}`}
            </Alert>
          )}
        </Box>
      )}
      {roster?.canFinalize === true && action !== 'finalize' && (
        <Box sx={{ mb: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              setAction('finalize');
              setErrorKey(null);
              setMessage(null);
            }}
          >
            {translate(locale, 'roster.finalize')}
          </Button>
        </Box>
      )}
      {roster?.canFinalize === true && action === 'finalize' && (
        <Stack
          component="form"
          spacing={1.5}
          sx={{ maxWidth: 480, mb: 1.5 }}
          onSubmit={(event) => {
            event.preventDefault();
            void finalizeRoster();
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {translate(locale, 'roster.finalize.hint')}
          </Typography>
          <TextField
            multiline
            minRows={2}
            size="small"
            label={translate(locale, 'roster.finalize.acknowledgement')}
            helperText={translate(locale, 'roster.finalize.acknowledgement.help')}
            value={statement}
            onChange={(event) => {
              setStatement(event.target.value);
            }}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button type="submit" variant="contained" size="small">
              {translate(locale, 'roster.finalize.submit')}
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAction(null);
              }}
            >
              {translate(locale, 'roster.close')}
            </Button>
          </Box>
        </Stack>
      )}
      {roster?.canReopen === true && action !== 'reopen' && (
        <Box sx={{ mb: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setAction('reopen');
              setErrorKey(null);
              setMessage(null);
            }}
          >
            {translate(locale, 'roster.reopen')}
          </Button>
        </Box>
      )}
      {roster?.canReopen === true && action === 'reopen' && (
        <Stack
          component="form"
          spacing={1.5}
          sx={{ maxWidth: 480, mb: 1.5 }}
          onSubmit={(event) => {
            event.preventDefault();
            void reopenRoster();
          }}
        >
          <TextField
            required
            multiline
            minRows={2}
            size="small"
            label={translate(locale, 'roster.reopen.reason')}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={reopenRequest === null}
            >
              {translate(locale, 'roster.reopen.submit')}
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAction(null);
              }}
            >
              {translate(locale, 'roster.close')}
            </Button>
          </Box>
        </Stack>
      )}
      {roster?.canChange === true && (
        <Stack
          component="form"
          key={formKey}
          spacing={1.5}
          sx={{ maxWidth: 480, mb: 1 }}
          onSubmit={(event) => {
            event.preventDefault();
            void add();
          }}
        >
          <RemoteSelect
            label={translate(locale, 'roster.field.athlete')}
            noMatchesText={translate(locale, 'roster.noAthletes')}
            load={findAthletes}
            onSelect={(selectedAthleteId) => {
              setAthleteId(selectedAthleteId);
            }}
          />
          <TextField
            required
            select
            size="small"
            label={translate(locale, 'roster.field.role')}
            value={form.role}
            onChange={(event) => {
              setForm({ ...form, role: rosterRoleOf(event.target.value) });
            }}
          >
            {roleOptions}
          </TextField>
          <TextField
            size="small"
            label={translate(locale, 'roster.field.shirt')}
            value={form.shirtNumber}
            onChange={(event) => {
              setForm({ ...form, shirtNumber: event.target.value });
            }}
            slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 2 } }}
          />
          <Box>
            <Button type="submit" variant="contained" size="small" disabled={addRequest === null}>
              {translate(locale, 'roster.add')}
            </Button>
          </Box>
        </Stack>
      )}
      <Button size="small" onClick={onClose}>
        {translate(locale, 'roster.close')}
      </Button>
    </Box>
  );
}
